import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Iconify from '../../ui-component/iconify';
import TableStyle from '../../ui-component/TableStyle';
import { useState, useEffect } from 'react';
import {
  Stack,
  Button,
  Container,
  Typography,
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Paper,
  Badge
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import { getApi, postApi, deleteManyApi } from 'views/services/api';
import DeleteItem from './Components/DeleteItem';
import AddItems from './AddItems';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { useNavigate } from 'react-router';
import ShowSeparateFoodBill from './ShowSeparateFoodBill';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import sampleFile from 'assets/sampleFile/sampleFile.xlsx';

const Restaurant = () => {
  const navigate = useNavigate();
  const [openAdd, setOpenAdd] = useState(false);
  const [itemsData, setItemsData] = useState([]);
  const [firstTabOption, setFirstTabOption] = useState('1');
  const [openDelete, setOpenDelete] = useState(false);
  const [id, setId] = useState('');
  const [currentItem, setCurrentItem] = useState(null);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || 'USD';
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const fetchItemsData = async () => {
    try {
      const response = await getApi(`api/restaurant/viewallitems/${hotel?.hotelId}`);
      const formattedData = response?.data?.restaurantData.map((item) => ({
        ...item,
        itemImage: item.itemImage.split('/').pop()
      }));
      setItemsData(formattedData);
    } catch (error) {
      console.error('Error al obtener items:', error);
      toast.error('Error al cargar los items del menú');
    }
  };

  useEffect(() => {
    fetchItemsData();
  }, [openAdd, openDelete]);

  const handleOpenDeleteItem = (itemId) => {
    setId(itemId);
    setOpenDelete(true);
  };

  const handleOpenAdd = () => {
    setCurrentItem(null);
    setOpenAdd(true);
  };

  const handleCloseAdd = () => {
    setCurrentItem(null);
    setOpenAdd(false);
  };

  const handleCloseDeleteItem = () => {
    setOpenDelete(false);
  };

  const handleMakeOrder = () => {
    navigate('/dashboard/separatefood/addfood');
  };

  const handleFirstTabChange = (event, newValue) => {
    setFirstTabOption(newValue);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const response = await postApi(`api/restaurant/importitems/${hotel?.hotelId}`, jsonData);
        if (response.status === 200) {
          toast.success('Items importados exitosamente');
          fetchItemsData();
          handleCloseImportModal();
        } else {
          toast.error('Error al importar items');
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al procesar el archivo');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleOpenMenu = () => {
    const hotelId = hotel?.hotelId;
    const menuUrl = `${window.location.origin}/menu.html?hotelId=${hotelId}`;
    window.open(menuUrl, '_blank');
  };

  const handleEditItem = (itemId) => {
    const item = itemsData.find((item) => item._id === itemId);
    setCurrentItem(item);
    setOpenAdd(true);
  };

  const handleRowSelectionChange = (newRowSelectionModel) => {
    setRowSelectionModel(newRowSelectionModel);
  };

  const handleDeleteSelectedItems = () => {
    setOpenConfirmDelete(true);
  };

  const handleConfirmDeleteItems = async () => {
    try {
      await deleteManyApi(`api/restaurant/deletemany`, { data: { ids: rowSelectionModel } });
      toast.success('Items eliminados exitosamente');
      setRowSelectionModel([]);
      fetchItemsData();
    } catch (error) {
      console.error('Error al eliminar items', error);
      toast.error('Error al eliminar los items');
    } finally {
      setOpenConfirmDelete(false);
    }
  };

  const columns = [
    {
      field: 'itemImage',
      headerName: 'Imagen',
      flex: 0.8,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Avatar
            src={`${baseUrl}api/uploads/restaurant/Item/${params.value}`}
            alt={params.row.itemName}
            variant="rounded"
            sx={{
              width: 50,
              height: 50,
              border: '2px solid',
              borderColor: 'divider',
              boxShadow: 1
            }}
          />
        </Box>
      )
    },
    {
      field: 'category',
      headerName: 'Categoría',
      flex: 1,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="primary" variant="outlined" sx={{ textTransform: 'capitalize', fontWeight: 500 }} />
      )
    },
    {
      field: 'itemName',
      headerName: 'Nombre del Item',
      flex: 1.5,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'amount',
      headerName: 'Precio',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="primary.main">
          {currency} {params.value.toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'inventoryData',
      headerName: 'Stock Disponible',
      flex: 1,
      renderCell: (params) => {
        if (!params.row.inventoryData) {
          return <Chip label="No enlazado" size="small" variant="outlined" color="default" sx={{ fontStyle: 'italic', opacity: 0.7 }} />;
        }
        const { stockQuantity, minStockAlert, unitMeasure } = params.row.inventoryData;
        const isLow = stockQuantity <= minStockAlert;
        return (
          <Chip 
            label={`${stockQuantity} ${unitMeasure}`} 
            color={isLow ? 'error' : 'secondary'} 
            variant={isLow ? 'filled' : 'outlined'} 
            size="small" 
            sx={{ fontWeight: 'bold' }} 
          />
        );
      }
    },
    {
      field: 'action',
      headerName: 'Acciones',
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={() => handleEditItem(params.row._id)}
            sx={{
              bgcolor: 'primary.lighter',
              color: 'primary.main',
              '&:hover': { bgcolor: 'primary.light' }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpenDeleteItem(params.row._id)}
            sx={{
              bgcolor: 'error.lighter',
              color: 'error.main',
              '&:hover': { bgcolor: 'error.light' }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      )
    }
  ];

  const handleOpenImportModal = () => {
    setOpenImportModal(true);
  };

  const handleCloseImportModal = () => {
    setOpenImportModal(false);
  };

  return (
    <>
      <AddItems open={openAdd} handleClose={handleCloseAdd} currentItem={currentItem} />
      <DeleteItem open={openDelete} handleClose={handleCloseDeleteItem} id={id} />

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 400 }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: 'error.main' }}>
                <DeleteIcon />
              </Avatar>
              <Typography variant="h6" fontWeight={600}>
                Confirmar Eliminación
              </Typography>
            </Stack>
            <IconButton onClick={() => setOpenConfirmDelete(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1">
            ¿Estás seguro de que deseas eliminar {rowSelectionModel.length} {rowSelectionModel.length === 1 ? 'item' : 'items'}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenConfirmDelete(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDeleteItems} variant="contained" color="error" sx={{ borderRadius: 2 }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de importación */}
      <Dialog
        open={openImportModal}
        onClose={handleCloseImportModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <UploadFileIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Importar Items del Menú
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Descarga la plantilla o sube tu archivo
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleCloseImportModal} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <Paper
              sx={{
                p: 2,
                border: '2px dashed',
                borderColor: 'primary.main',
                bgcolor: 'primary.lighter',
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="primary.main" fontWeight={500} gutterBottom>
                📋 Instrucciones:
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                1. Descarga el archivo de muestra
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                2. Completa la información de tus items
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                3. Sube el archivo completado
              </Typography>
            </Paper>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<DownloadIcon />}
                component="a"
                href={sampleFile}
                download
                sx={{ borderRadius: 2, py: 1.5 }}
              >
                Descargar Plantilla
              </Button>

              <Button variant="contained" fullWidth component="label" startIcon={<UploadFileIcon />} sx={{ borderRadius: 2, py: 1.5 }}>
                Subir Archivo
                <input type="file" accept=".xlsx, .xls" hidden onChange={handleFileUpload} />
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseImportModal} variant="outlined" sx={{ borderRadius: 2 }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contenido Principal */}
      <Container maxWidth="xl">
        <TabContext value={firstTabOption}>
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', boxShadow: 2 }}>
                  <RestaurantMenuIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    Gestión de Restaurante
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Administra tu menú y órdenes de comida
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <TabList onChange={handleFirstTabChange} textColor="secondary" indicatorColor="secondary">
                    <Tab icon={<FastfoodIcon />} iconPosition="start" label="Items del Menú" value="1" sx={{ fontWeight: 600 }} />
                    <Tab icon={<ReceiptLongIcon />} iconPosition="start" label="Facturas Separadas" value="2" sx={{ fontWeight: 600 }} />
                  </TabList>

                  <Stack direction="row" spacing={1.5} sx={{ py: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Iconify icon="eva:plus-fill" />}
                      onClick={handleMakeOrder}
                      sx={{ borderRadius: 2 }}
                    >
                      Crear Orden
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Iconify icon="eva:plus-fill" />}
                      onClick={handleOpenAdd}
                      sx={{ borderRadius: 2 }}
                    >
                      Nuevo Item
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<UploadFileIcon />}
                      onClick={handleOpenImportModal}
                      sx={{ borderRadius: 2 }}
                    >
                      Importar
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<MenuBookIcon />} onClick={handleOpenMenu} sx={{ borderRadius: 2 }}>
                      Ver Menú
                    </Button>
                    <Badge badgeContent={rowSelectionModel.length} color="error">
                      <Button
                        variant="contained"
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        disabled={rowSelectionModel.length === 0}
                        onClick={handleDeleteSelectedItems}
                        sx={{ borderRadius: 2 }}
                      >
                        Eliminar
                      </Button>
                    </Badge>
                  </Stack>
                </Stack>
              </Box>

              <TabPanel value="1" sx={{ p: 0 }}>
                <TableStyle>
                  <Box width="100%">
                    <Card
                      sx={{
                        height: '600px',
                        border: 'none',
                        borderRadius: 0,
                        boxShadow: 'none'
                      }}
                    >
                      <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="h6" fontWeight={600}>
                            Items del Menú
                          </Typography>
                          <Chip icon={<FastfoodIcon />} label={`${itemsData?.length} Items`} color="primary" variant="outlined" />
                        </Stack>
                      </Box>
                      <DataGrid
                        rows={itemsData}
                        columns={columns}
                        checkboxSelection
                        getRowId={(row) => row._id}
                        onRowSelectionModelChange={handleRowSelectionChange}
                        rowSelectionModel={rowSelectionModel}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                          toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 }
                          }
                        }}
                        rowHeight={70}
                        disableRowSelectionOnClick
                        sx={{
                          border: 'none',
                          '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                          },
                          '& .MuiDataGrid-columnHeaders': {
                            bgcolor: 'grey.100',
                            borderBottom: '2px solid',
                            borderColor: 'divider',
                            fontSize: '0.9rem',
                            fontWeight: 700
                          },
                          '& .MuiDataGrid-row:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      />
                    </Card>
                  </Box>
                </TableStyle>
              </TabPanel>

              <TabPanel value="2" sx={{ p: 3 }}>
                <ShowSeparateFoodBill />
              </TabPanel>
            </Paper>
          </Box>
        </TabContext>
      </Container>
    </>
  );
};

export default Restaurant;
